import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Handshake, RefreshCw, ArrowUp, ArrowDown, Trash2, Plus, Check, Loader2, Star } from 'lucide-react';
import { Switch } from '../ui/switch';
import { resolveMedia } from '../../lib/media';
import UploadMediaButton from './UploadMediaButton';
import { uid } from '../../lib/uid';

export const DEFAULT_SPONSORS = [
  { name: 'BM Records', url: 'https://www.bmrecords.eu/', logo: '' },
  { name: 'Hip Hop Era Foundation', url: 'https://hiphoperafoundation.it/', logo: '' },
  { name: 'Hip Hop Kemp', url: 'https://www.hiphopkemp.cz/it/', logo: '' },
  { name: 'All Bars Game', url: 'https://www.youtube.com/@All_Bars_Game', logo: '' },
  { name: 'The Dome', url: 'https://www.youtube.com/@THEDOMETV/videos', logo: '' },
  { name: 'Londinese', url: 'https://londinese.it/', logo: '' },
  { name: 'Conditio Et Intellectus', url: 'https://hhcei.eu/', logo: '' },
  { name: 'Il Rappuso', url: 'https://www.ilrappuso.com/', logo: '' },
  { name: 'Say Waaad?', url: 'https://www.instagram.com/saywaaad/', logo: '' },
  { name: '4P', url: 'https://www.instagram.com/4thpotere/', logo: '' },
];

const SponsorsPanel = ({ items, setItems, onSave, onReload, saving, loaded }) => {
  const update = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    setItems(next);
  };
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 size={24} className="animate-spin mr-2" /> Caricamento sponsor...
      </div>
    );
  }

  const missingLogos = items.filter(i => !i.logo).length;

  return (
    <div className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }} data-testid="sponsors-panel">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-white font-bold flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
            <Handshake size={18} className="text-emerald-500" /> LOGHI SPONSOR (FOOTER)
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            Nome, link e logo (carica un file PNG/SVG/JPG o incolla l'URL). I loghi sostituiscono il banner statico in fondo a ogni pagina e compaiono nella pagina Sponsor. Con MAIN il logo appare grande in cima alla pagina Sponsor.
            {missingLogos > 0 && <span className="text-yellow-500 ml-1">{missingLogos} senza logo: verrà mostrato il nome.</span>}
          </p>
        </div>
        <Button onClick={onReload} size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800" data-testid="sponsors-reload-btn">
          <RefreshCw size={14} />
        </Button>
      </div>

      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={it._uid || idx} className="flex items-start gap-2 p-2 rounded border border-gray-800 bg-gray-900/50" data-testid={`sponsor-item-${idx}`}>
            <span className="text-gray-500 text-xs w-6 text-center font-mono pt-2">{idx + 1}</span>
            <div className="w-20 h-14 shrink-0 rounded overflow-hidden bg-white/95 border border-gray-800 flex items-center justify-center p-1">
              {it.logo
                ? <img src={resolveMedia(it.logo)} alt="" className="max-w-full max-h-full object-contain" />
                : <span className="text-[9px] text-gray-500 text-center leading-tight">nessun logo</span>}
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5 min-w-0">
              <Input data-testid={`sponsor-name-${idx}`} value={it.name || ''} onChange={e => update(idx, 'name', e.target.value)}
                placeholder="Nome sponsor" className="bg-gray-900 border-gray-700 text-white font-bold text-sm" />
              <div className="flex items-center gap-2">
                <Input data-testid={`sponsor-url-${idx}`} value={it.url || ''} onChange={e => update(idx, 'url', e.target.value)}
                  placeholder="https://sito-sponsor.it" className="bg-gray-900 border-gray-700 text-white font-mono text-xs flex-1" />
                <label className="flex items-center gap-1.5 text-[11px] text-gray-300 whitespace-nowrap cursor-pointer select-none" title="Main partner: logo grande in cima alla pagina Sponsor">
                  <Star size={12} className={it.featured ? 'text-yellow-400 fill-yellow-400' : 'text-gray-500'} />
                  MAIN
                  <Switch data-testid={`sponsor-featured-${idx}`} checked={!!it.featured} onCheckedChange={v => update(idx, 'featured', v)}
                    className="scale-75 data-[state=checked]:bg-yellow-500 data-[state=unchecked]:bg-gray-700" />
                </label>
              </div>
              <Input data-testid={`sponsor-desc-${idx}`} value={it.desc || ''} onChange={e => update(idx, 'desc', e.target.value)}
                placeholder="Breve descrizione (mostrata nella pagina Sponsor)" className="bg-gray-900 border-gray-700 text-white text-xs sm:col-span-2" />
              <div className="flex gap-1.5 sm:col-span-2">
                <Input data-testid={`sponsor-logo-${idx}`} value={it.logo || ''} onChange={e => update(idx, 'logo', e.target.value)}
                  placeholder="URL logo — oppure CARICA" className="bg-gray-900 border-gray-700 text-white font-mono text-xs flex-1" />
                <UploadMediaButton idx={`sponsor-${idx}`} accept="image/*" onUploaded={(url) => update(idx, 'logo', url)} />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <button type="button" data-testid={`sponsor-up-${idx}`} onClick={() => move(idx, -1)} disabled={idx === 0}
                className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30"><ArrowUp size={13} /></button>
              <button type="button" data-testid={`sponsor-down-${idx}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1}
                className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30"><ArrowDown size={13} /></button>
              <button type="button" data-testid={`sponsor-delete-${idx}`} onClick={() => setItems(items.filter((_, i) => i !== idx))}
                className="p-1.5 rounded bg-red-900/50 hover:bg-red-800 text-red-400"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        <Button data-testid="sponsor-add-btn" onClick={() => setItems([...items, { _uid: uid(), name: '', url: '', logo: '', desc: '', featured: false }])}
          size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
          <Plus size={14} className="mr-1" /> AGGIUNGI SPONSOR
        </Button>
        <Button data-testid="sponsors-save-btn" onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
          {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
          SALVA SPONSOR
        </Button>
      </div>
    </div>
  );
};

export default SponsorsPanel;
